import { useState } from "react";
import { FormData } from "@/types/inventory";
import { toast } from "sonner";

export const useAddItemState = (initialState: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const [activeTab, setActiveTab] = useState<string>("manual");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [pendingItems, setPendingItems] = useState<FormData[]>([]);
  const [lastScannedCode, setLastScannedCode] = useState<string | null>(null);
  
  const [formData, setFormData] = useState<FormData>({
    name: "",
    quantity: 1,
    category: "",
    expiration: new Date(),
    location: "",
    notes: "",
    image_url: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({
    name: "",
    quantity: "",
    category: "",
    expiration: "",
  });

  const handleFormChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ""
      }));
    }
    
    setIsDuplicate(false);
  };

  const handleBarcodeDetected = (qrcode: string, productInfo?: any) => {
    console.log("QR code detected in context:", qrcode);
    
    // Store the last scanned code
    setLastScannedCode(qrcode);
    
    let updatedFormData = {
      ...formData
    };
    
    try {
      // Try to parse as JSON first
      if (qrcode.startsWith('{') && qrcode.endsWith('}')) {
        const parsedData = JSON.parse(qrcode);
        updatedFormData = {
          ...updatedFormData,
          name: parsedData.name || updatedFormData.name,
          quantity: parsedData.quantity || updatedFormData.quantity,
          category: parsedData.category || updatedFormData.category,
          expiration: parsedData.expiryDate || updatedFormData.expiration,
        };
      } 
      // Then try common text formats
      else if (qrcode.includes('Product:') || qrcode.includes('Expiry:')) {
        const lines = qrcode.split('\n');
        const name = lines.find(l => l.includes('Product:'))?.replace('Product:', '').trim();
        const expiry = lines.find(l => l.includes('Expiry:'))?.replace('Expiry:', '').trim();
        
        if (name) updatedFormData.name = name;
        if (expiry) updatedFormData.expiration = expiry;
      } 
      // For unstructured data, store it in notes
      else {
        updatedFormData = {
          ...updatedFormData,
          name: qrcode.length > 20 
            ? `Scanned Item: ${qrcode.substring(0, 20)}...` 
            : `Scanned Item: ${qrcode}`,
          notes: `QR Code Data:\n${qrcode}\n${updatedFormData.notes || ''}`.trim(),
          category: updatedFormData.category || "Unclassified"
        };
      }
    } catch (error) {
      console.error("Error parsing QR data:", error);
      // If parsing fails, treat as unstructured data
      updatedFormData = {
        ...updatedFormData,
        name: `Scanned Item: ${qrcode.substring(0, 20)}...`,
        notes: `QR Code Data:\n${qrcode}\n${updatedFormData.notes || ''}`.trim(),
        category: "Unclassified"
      };
    }
    
    setFormData(updatedFormData);
    
    toast.success(`QR code detected! Data filled in the form.`, {
      description: `Code: ${qrcode.substring(0, 25)}${qrcode.length > 25 ? '...' : ''}`,
    });
    
    setActiveTab("manual");
  };

  const handleItemsExtracted = (items: any[]) => {
    if (items.length === 0) return;
    
    const formattedItems = items.map(item => ({
      name: item.name || "Unknown item",
      quantity: item.quantity || 1,
      category: item.category || formData.category || "General",
      expiration: item.expiryDate || new Date(),
      location: formData.location || "",
      notes: item.notes || "",
      image_url: ""
    }));
    
    setPendingItems(formattedItems);
    
    if (formattedItems.length > 0) {
      setFormData(formattedItems[0]);
      toast.success(`${formattedItems.length} items extracted from receipt!`);
    }
    
    setActiveTab("manual");
  };

  return {
    activeTab,
    setActiveTab,
    isProcessing,
    setIsProcessing,
    isOffline,
    setIsOffline,
    isDuplicate,
    setIsDuplicate,
    pendingItems,
    setPendingItems,
    formData,
    setFormData,
    errors,
    setErrors,
    handleFormChange,
    handleBarcodeDetected,
    handleItemsExtracted,
    lastScannedCode,
    setLastScannedCode
  };
};
